import React, { useContext, useMemo } from 'react';
import { useProductBandQuery } from '@/api/product/bands';
import { InfoList, InfoListData } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	AccountId,
	RegistrationCompositeDetailsResponse,
	RegistrationCompositeId,
	type SelectedAccountResponse,
} from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import styles from './ApplicationDetail.module.scss';
import { EditFilterUnit } from './FilterUnit/EditFilterUnit';
import filterStyles from './FilterUnit/FilterUnit.module.scss';
import { RegisterFilterUnit } from './FilterUnit/RegisterFilterUnit';
import { SelectionByApplication } from './FilterUnit/SelectionByApplication';
import { SpareParts } from './FilterUnit/SpareParts';

type FilterInsert = {
	name?: string | null;
	articleNo?: string | null;
	lastChangedDate?: string;
};

interface FilterUnitOverviewProps {
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	application?: RegistrationCompositeDetailsResponse;
	selectedCustomer?: SelectedAccountResponse;
}

export const FilterUnitOverview: React.FunctionComponent<FilterUnitOverviewProps> = ({
	accountId,
	registrationCompositeId,
	selectedCustomer,
	application,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);

	const filterUnitArticleIds: string[] = useMemo(() => {
		if (!application?.filterUnit) return [];

		const { articleNumber, inserts, spareParts } = application?.filterUnit ?? {};

		const filterUnitIds = [articleNumber, ...inserts.map((x) => x.articleNumber)].filter(Boolean) as string[];
		const sparePartIds = (spareParts ?? []).map((s) => String(s?.articleNumber));

		return [...filterUnitIds, ...sparePartIds];
	}, [application?.filterUnit]);

	const { data: productItems } = useProductBandQuery(
		filterUnitArticleIds,
		filterUnitArticleIds.length > 0,
		settings?.data.searchSettings,
	);

	const cultureCode = settings?.data?.cultureCode ?? '';

	const formatDate = (filterUnitDate?: string | null, cultureCode?: string) => {
		if (typeof filterUnitDate !== 'string') return undefined;

		const date = new Date(filterUnitDate);
		if (isNaN(date.getTime())) return undefined;

		const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
		return new Intl.DateTimeFormat(cultureCode, options).format(date);
	};

	const filterInsertData: FilterInsert[] = useMemo(() => {
		if (!application?.filterUnit) {
			return [];
		}
		return application?.filterUnit?.inserts.map((x) => {
			return {
				insertName: x.name,
				articleNo: x.articleNumber,
				lastChangedDate: formatDate(x.lastChangedDate, cultureCode),
			};
		});
	}, [application?.filterUnit, cultureCode]);

	const filterUnitViewData: InfoListData = useMemo(() => {
		if (!application?.filterUnit) {
			return {};
		}
		return {
			name: application?.filterUnit?.name,
			articleNo: application?.filterUnit?.articleNumber,
			batchNo: application?.filterUnit?.inserts?.[0]?.batchNumber,
			orderNo: application?.filterUnit?.orderNo,
			dateOfSale: formatDate(application?.filterUnit?.inserts?.[0]?.saleDate, cultureCode),
			installationDate: formatDate(application?.filterUnit?.inserts?.[0]?.installationDate, cultureCode),
		};
	}, [application?.filterUnit, cultureCode]);

	return (
		<section>
			<div className={styles.section}>
				<header className={filterStyles.sectionHeader}>
					<h2 className={filterStyles.sectionHeadline}>
						{translations?.data.applications.filterUnitHeadline}
					</h2>

					{application?.filterUnit ? (
						<EditFilterUnit
							ariaControlId="edit-filterUnit"
							accountId={accountId}
							registrationCompositeId={registrationCompositeId}
							filterUnit={application?.filterUnit}
							productItems={productItems}
						/>
					) : (
						<RegisterFilterUnit
							ariaControlId="register-filterUnit"
							accountId={accountId}
							registrationCompositeId={registrationCompositeId}
						/>
					)}
				</header>

				<div className={filterStyles.table}>
					<InfoList
						data={filterUnitViewData}
						listOnly
						translations={translations?.data.filterRegistrations}
					/>
					<div className={filterStyles.headline}>
						{translations?.data.filterRegistrations.filterInsertHeadline}
					</div>
					{filterInsertData.map((insert) => {
						return (
							<InfoList
								data={insert}
								translations={translations?.data.filterRegistrations}
								listOnly
								key={insert.articleNo}
							/>
						);
					})}
				</div>

				{application?.filterUnit && (
					<SpareParts
						accountId={accountId}
						registrationCompositeId={registrationCompositeId}
						filterUnit={application?.filterUnit}
						productItems={productItems}
					/>
				)}
				{selectedCustomer?.id && (
					<SelectionByApplication
						accountId={accountId}
						selectedCustomerId={selectedCustomer.id}
						segment={application?.segmentSystemName ?? ''}
						application={application?.applicationSystemName ?? ''}
					/>
				)}
			</div>
		</section>
	);
};
